import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import AddressInput from "@/Shared/Locations/Inputs/LocationsAddressInput";
import { locationsErrorMessagesForLocation } from "@/Shared/Locations/Validators/locationsErrorMessagesForLocation";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

interface ViewShipmentsModifyAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: Location;
  onFinished: (updatedLocation: Location) => void;
}

// Post-booking "Change Address" dialog. Validation gates the Done button, so
// the buttons live in the body rather than the default dialog footer.
function ViewShipmentsModifyAddressDialog_(
  props: ViewShipmentsModifyAddressDialogProps,
) {
  const [location, setLocation] = useState(props.location);
  const [forceValidation, setForceValidation] = useState(false);
  const locationErrorMessages = locationsErrorMessagesForLocation(
    location,
    false,
  );

  function isValid() {
    if (locationErrorMessages.address.addressLine !== undefined) {
      return false;
    }

    if (locationErrorMessages.businessName !== undefined) {
      return false;
    }

    return true;
  }

  function onDone() {
    if (isValid()) {
      props.onFinished(location);
      props.onOpenChange(false);
    } else {
      setForceValidation(true);
    }
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Change Address"
      description="Enter updated details for this shipment. Changes will also be saved to your address book"
    >
      <ControlPresentationProvider presentation="underlined">
        <Stack gap={6}>
          <AddressInput
            enabled={true}
            businessName={location.businessName || ""}
            businessNameChanged={function (newBusinessName) {
              setLocation({
                ...location,
                businessName: newBusinessName,
              });
            }}
            locationType={location.locationType}
            distributionWarehouseBrand={location.distributionWarehouseBrand}
            address={location.address}
            addressChanged={function (newAddress) {
              const newLocation = {
                ...location,
                address: {
                  ...location.address,
                  ...newAddress,
                },
              } as Location;
              setLocation(newLocation);
            }}
            forceValidation={forceValidation}
            businessNameErrorMessages={locationErrorMessages.businessName}
            addressErrorMessages={locationErrorMessages.address}
            addressOptions={[]}
            allowCityAndPostalCodeEditing={false}
            warnAboutPotentialBusinessNameErrors={true}
          />
          <Row gap={2} justify="between">
            <Button onClick={() => props.onOpenChange(false)}>Cancel</Button>

            <Button primary onClick={onDone}>
              Done
            </Button>
          </Row>
        </Stack>
      </ControlPresentationProvider>
    </Dialog>
  );
}

// Awaitable wrapper matching the legacy useModifyAddressDialog contract:
// resolves the updated Location, or undefined on cancel/close.
export function useModifyAddressDialog(): {
  show: (location: Location) => Promise<Location | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const resolverRef = useRef<
    ((updatedLocation: Location | undefined) => void) | undefined
  >(undefined);

  function show(locationToModify: Location): Promise<Location | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setLocation(locationToModify);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(updatedLocation: Location | undefined) {
    resolverRef.current?.(updatedLocation);
    resolverRef.current = undefined;
  }

  const dialogs = location !== undefined && (
    <ViewShipmentsModifyAddressDialog_
      key={`view-shipments-modify-address-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
      location={location}
      onFinished={settle}
    />
  );

  return { show, dialogs };
}
